import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Action, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { camelize } from 'humps';
import { map, path } from 'ramda';
import { combineEpics, StateObservable } from 'redux-observable';
import {
  of,
  from,
  concat,
  mergeMap,
  switchMap,
  tap,
  catchError,
  EMPTY,
  Observable,
} from 'rxjs';
import { ignoreElements, filter } from 'rxjs/operators';

import { analyticsStore } from '@ezugi/analytics/sendAnalytics';
import {
  CODES,
  GAME_NOT_SUPPORTED_FOR_DEVICE,
  TABLE_IS_CLOSED,
} from '@ezugi/constants/src/errors';
import { BLACKJACK_VIDEOCHAT_ID } from '@ezugi/constants/src/gameIds';
import { GenericPayloadType } from '@ezugi/types';
import { setTheme, Skin } from '@ezugi/ui/theme';
import {

  DeviceInfo, handleError, HTTPError, NetworkPerformanceTracker, queryParams, ss,
} from '@ezugi/utils';
import { QueryStringsReturnType } from '@ezugi/utils/src/functions/queryParams/queryParams.types';
import { ErrorParams } from '@ezugi/utils/src/http/httpErrors';

import { addEpicSubject, addReducerSubject } from '../../../streams';
import { formatPayout } from '../../../util/formatPayout';
import { getPayouts, handlePayoutsError } from '../../../util/getPayouts';
import { logGameLoadFailure } from '../../../util/logUtils';
import {
  authActions,
  configActions,
  errorActions,
  localeActions,
  gameActions,
  pingActions,
  userActions,
  settingsActions,
  lobbyActions,
  tableActions,
  externalGameTypesActions,
  videoActions,
} from '../../actions';
import { configActionsFromSlice } from '../../reducers/config';
import { UserState } from '../../reducers/user';
import { formatSelector, isEvoPlayerSelector } from '../../selectors';
import { RootState } from '../../types';
import { injectAnalytics } from '../analytics';
import {
  pluckGeneral,
  pluckBjMaximumSeatsPerPLayer,
  pluckGameTypeData,
  getInitialChip,
  pluckGroups,
  pluckTable,
  pluckAmountDisplay,
  pluckWidgets,
  pluckChat,
  pluckUserData,
  pluckGameResults,
  pluckHome,
  pluckWinnerList,
  pluckFilters,
  pluckBetsConfig,
  pluckCashier,
  pluckExternalGameTypes,
  pluckGameServerConfig,
  getABTestingPayload,
} from '../auth/utils';
import { fetchTableConfig } from './fetch';
import {
  pluckGame,
  pluckAuthParams,
  pluckDefinitions,
  pluckFeatureFlags,
  pluckGameTypes,
  pluckIsEvoPlayer,
  pluckLanguage,
  pluckLocale,
  pluckOssConfig,
  pluckPreloader,
  pluckSkin,
  pluckSkinColors,
  pluckComplianceConfig,
  pluckStreamsData,
  pluckRetail,
  pluckRedirectUrl,
  pluckBonusConfig,
} from './utils';

const { retail } = configActions;
const { auth } = authActions;

const config = process.env.NODE_ENV !== 'test'
  ? configActions.config : (configActions.config || configActionsFromSlice);

const gameName = import.meta.env.VITE_GAME_NAME || '';
const VITE_IS_BUNDLE = import.meta.env.VITE_IS_BUNDLE;

const { locale } = localeActions;
const { game } = gameActions;
const { ping } = pingActions;
const { user } = userActions;
const { settings } = settingsActions;
const { table } = tableActions;
const { lobby } = lobbyActions;
const { externalGameTypes } = externalGameTypesActions;
const { video } = videoActions;

export const configEpic = (
  action$: Observable<Action>,
  store$: StateObservable<RootState>
) => action$.pipe(
  filter(config.request.match),
  switchMap(({ payload }) => {
    NetworkPerformanceTracker.getInstance().startTracking();
    const hasAlreadyFetchedConfig = payload?.config;

    return hasAlreadyFetchedConfig
      ? Promise.resolve(payload.config)
      : from(fetchTableConfig({ localeGroups: payload?.localeGroups }));
  }),
  tap(({ result: data }) => {
    document.documentElement.lang = data.language;
  }),
  mergeMap(({ result: data }) => {
    const { outgoing } = data;
    const { params }: QueryStringsReturnType = queryParams;

    const userData: UserState = pluckUserData(data);

    const {
      setEnabled,
      setBasePayload,
      setDatadogEnabled,
      setABTestingPayload,
    } = analyticsStore.getState();
    setEnabled(outgoing.outgoingCalls);
    setBasePayload(data);
    setDatadogEnabled(outgoing.dataDogEnabled);
    if (!outgoing.dataDogEnabled) {
      datadogLogs.logger.setHandler('silent');
      datadogRum.stopSession();
    }
    setABTestingPayload(getABTestingPayload(data));

    if (outgoing.outgoingCalls) {
      injectAnalytics();
    }

    // We can not run games that are using NanoPlayer in Samsung Internet Browser because NanoPlayer doesn't support this browser.
    if (DeviceInfo.isSamsungBrowser && !isEvoPlayerSelector(store$.value)) {
      return of(
        errorActions.error.set({
          code: CODES[GAME_NOT_SUPPORTED_FOR_DEVICE],
          message: 'Samsung browser is not supported',
          title: 'errors.samsung_browser_reject_title',
          description: 'errors.samsung_browser_reject_desc',
          actionLabel: 'general.go_back',
        })
      );
    }

    /**
       * Apple requires all assets to be static in iOS bundles
       * See https://developer.apple.com/news/?id=12232019b
       */
    const tableData = VITE_IS_BUNDLE
      ? Object.entries(pluckTable(data)).reduce(
        (acc: Record<string, any>, entry: [string, any]) => {
          if (entry[0] === 'limits') {
            return {
              ...acc,
              [entry[0]]: Object.entries(entry[1]).reduce(
                (accumulator, current: Record<string, any>) => ({
                  ...accumulator,
                  [current[0]]: {
                    ...current[1],
                    chips: current[1].chips.map(
                      ({ img, ...rest }: { img: string }) => ({
                        ...rest,
                        img: `../CustomAssets/${
                              img.split('/CustomAssets/')[1]
                            }`,
                      })
                    ),
                  },
                }),
                {}
              ),
            };
          }

          return {
            ...acc,
            [entry[0]]: entry[1],
          };
        },
        {}
      )
      : pluckTable(data);
    const groups = pluckGroups(data);

    const limits = path([ 'limits', params.limitId! ], tableData)
        || Object.values(tableData.limits)[0];
    const minBet = path([ 'Min_Bet' ], limits);

    const selectedChip = getInitialChip({ chips: limits.chips, minBet });

    const localeData = pluckLocale(data);
    const skin = pluckSkin(data) as Skin;
    const gameData = pluckGame(data);
    const ossConfig = pluckOssConfig(data);

    setTheme(skin);

    // eslint-disable-next-line eqeqeq
    if (
      !DeviceInfo.isHandheld
        && gameData.gameId === BLACKJACK_VIDEOCHAT_ID
    ) {
      return of(
        locale.set({
          lang: data.language,
          locales: localeData,
          loaded: true,
        }),
        config.update({ oss: ossConfig }),
        errorActions.error.set({
          code: CODES[GAME_NOT_SUPPORTED_FOR_DEVICE],
          title: 'errors.desktop_not_available_title',
          description: 'errors.desktop_not_available_description',
        })
      );
    }

    const general = pluckGeneral(data);

    const maxNoOfSeatsPerPlayer = pluckBjMaximumSeatsPerPLayer(data);
    const widgets = pluckWidgets(data);
    const home = pluckHome(data);
    const chat = pluckChat(data);
    const cashier = pluckCashier(data);
    const format = pluckAmountDisplay(data);
    const gameResults = pluckGameResults(data);
    const winnerList = pluckWinnerList(data);
    const filters = pluckFilters(data);
    const bets = pluckBetsConfig(data);
    const compliance = pluckComplianceConfig(data);
    const externalProviderTypes = pluckExternalGameTypes(data);
    const streams = pluckStreamsData(data);
    const gameServer = pluckGameServerConfig(data);
    const bonusConfig = pluckBonusConfig(data);

    const gameTypeData = (() => {
      try {
        return pluckGameTypeData(data);
      } catch {
        return {};
      }
    })();

    // stored in ss for situations in which the user no longer has a valid session (home config unavailable)
    // so that it can be used for redirecting to login page in GameError component
    if (widgets?.home && (home as any)?.homeUrl) {
      ss.put('home_url', (home as any).homeUrl);
    }

    return concat(
      of(
        config.set({
          agora: data?.agora,
          authParams: pluckAuthParams(data),
          definitions: pluckDefinitions(data),
          [camelize(gameName)]: gameTypeData,
          outgoingCalls: Boolean(outgoing?.outgoingCalls),
          skin,
          home,
          chat,
          cashier,
          gameResults,
          widgets,
          format,
          bonus: bonusConfig,
          winnerList,
          filters,
          bets,
          excludeGames: data.exclude_games,
          excludeTables: data.exclude_tables,
          responsibleGaming: {
            responsibleGamingMethod: 'url',
            responsibleGamingUrl: params.responsibleGamingUrl,
          },
          // TODO: delete after migration from legacy mobile header
          skinColors: pluckSkinColors(data),
          retailRedirectUrl: pluckRedirectUrl(data),
          preloader: pluckPreloader(data),
          gameTypes: pluckGameTypes(data),
          oss: pluckOssConfig(data),
          featureFlags: pluckFeatureFlags(data),
          compliance,
          general: {
            ...general,
            maxNoOfSeatsPerPlayer,
            language: pluckLanguage(data),
            isEvoPlayer: pluckIsEvoPlayer(data),
          },
          gameServer,
        }),
        locale.set({
          lang: data.language,
          locales: localeData,
          loaded: true,
        }),
        retail.set(pluckRetail(data) as Record<string, string>),
        externalGameTypes.set(externalProviderTypes),
        game.set({ ...gameData, selectedChip, limitId: limits.id }),
        user.set({ sessionStart: Date.now(), ...userData }),
        lobby.patch({ authGroups: groups }),
        table.set(tableData),
        settings.clear()
      ),
      tableData.active ? of(
        config.success(),
        auth.success(),
        ping.request(),
        video.request(streams)
      ) : of(errorActions.error.set({
        code: CODES[TABLE_IS_CLOSED],
      })),
    );
  }),
  catchError((error) => {
    const { setEnabled, setBasePayload } = analyticsStore.getState();
    injectAnalytics();
    setEnabled(true);
    setBasePayload();

    // We close DataDog if request fails
    datadogLogs.logger.setHandler('silent');
    datadogRum.stopSession();
    const setError: ErrorParams = {
      error,
      failAction:
          config.failure as unknown as ActionCreatorWithPayload<GenericPayloadType>,
      options: {
        httpErrorHandler: (err: HTTPError) => (pluckLocale(err.response)
          ? of(
            locale.set({
              locales: pluckLocale(err.response),
              loaded: true,
            }),
            config.update({ ...(err.response as any)?.initialConfig })
          )
          : EMPTY),
        clientErrorHandler: logGameLoadFailure,
      },
    };

    return handleError(setError);
  })
);

const chatEnabledEpic = (action$: Observable<Action>) => action$.pipe(
  filter(config.set.match),
  filter(({ payload }) => payload?.widgets?.chat),
  tap(async () => {
    const { default: chatReducer } = await import('../../reducers/chat');
    chatReducer
        && addReducerSubject.next({
          reducerPath: 'chat',
          reducer: chatReducer,
        });
    const { default: chatEpic } = await import('../chat');
    chatEpic && addEpicSubject.next(chatEpic);
  }),
  ignoreElements()
);

const autoplayEnabledEpic = (action$: Observable<Action>) => action$.pipe(
  filter(config.set.match),
  filter(({ payload }) => payload.featureFlags?.autoplay),
  tap(async () => {
    const { default: autoplayReducer } = await import(
      '../../reducers/autoplay'
    );
    addReducerSubject.next({
      reducerPath: 'autoplay',
      reducer: autoplayReducer,
    });
    const { default: autoplayEpic } = await import('../autoplay');
    addEpicSubject.next(autoplayEpic);
  }),
  ignoreElements()
);

export const payoutsEpic = combineEpics(
  (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
    filter(auth.success.match),
    switchMap(() => from(getPayouts(state$.value, false))),
    mergeMap(({ result: { payouts } }) => {
      const format = formatSelector(state$.value);
      const payoutsFormatted = map(
        (payout) => formatPayout(Number(payout), format?.payoutFormat),
        payouts
      );

      return of(config.update({ payouts: payoutsFormatted }));
    }),
    catchError(handlePayoutsError)
  )
);

export default combineEpics(configEpic, chatEnabledEpic, autoplayEnabledEpic);
